/**
 * PlayerControlMap — single-player variant of TeamControlMap (PMA-COCK-168).
 *
 * Reuses `ControlCard` (exported from `TeamControlMap.tsx`, no visual
 * change made to that file) fed by `computePlayerControlMap` instead of
 * `computeTeamControlMap` — identical thirds/lanes math and orientation
 * convention (home attacks x=120, away attacks x=0), filtered to one
 * player's own located events instead of a whole team's. Mounted inside
 * `PlayerCard`, after the pass map.
 */
import React, { useMemo } from "react";
import { ControlCard } from "./TeamControlMap";
import {
  computePlayerControlMap,
  type TeamSide,
} from "../../utils/teamControlMap";
import { resolveTeamPalette } from "../../../../lib/teamPalette";
import type { MatchEvent } from "../../../../store/useMatchLogStore";
import type { Team } from "../../types";

interface PlayerControlMapProps {
  team: Team;
  side: TeamSide;
  playerId: string;
  events: MatchEvent[];
  t: (key: string, fallback?: string) => string;
  "data-testid"?: string;
}

const PlayerControlMap: React.FC<PlayerControlMapProps> = ({
  team,
  side,
  playerId,
  events,
  t,
  "data-testid": testId = "player-control-map",
}) => {
  const data = useMemo(
    () => computePlayerControlMap(events, playerId, side),
    [events, playerId, side],
  );

  const color = useMemo(() => resolveTeamPalette(team).primary, [team]);

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <h4
        data-testid={`${testId}-title`}
        className="text-xs font-semibold text-slate-200 uppercase tracking-wider"
      >
        {t("analytics.playerControlMapTitle", "Player Control Map")}
      </h4>
      <ControlCard
        team={team}
        side={side}
        data={data}
        color={color}
        t={t}
        data-testid={`${testId}-card`}
      />
    </div>
  );
};

export default PlayerControlMap;
